import Parse from 'parse';

import { Finding, StepTask } from '@telespot/domain';
import {
  AlgorithmTopology,
  AnalysisTopology,
  AssetTopology,
  ParseFindingMapper,
  ParseMapper,
  PipelineTopology,
  SampleTopology,
} from '@telespot/infrastructure/parse';

export class TelespotFindingMapper extends ParseMapper {
  private readonly findingMapper: ParseFindingMapper;
  private readonly groupableFindingTypes = new Set([StepTask.TEXT, StepTask.CLASSIFICATION]);

  constructor(protected readonly parse: typeof Parse) {
    super(parse);
    this.findingMapper = new ParseFindingMapper(parse);
  }

  public toDomain(object: Parse.Object): Finding {
    return this.findingMapper.toDomain(object);
  }

  public fromDomain(findings: Finding[], sampleId: string): Parse.Object[] {
    const parseObjects: Parse.Object[] = [];
    const groupedFindings: Record<string, Finding[]> = {};
    const sharedAnalyses: Record<string, Parse.Object> = {};

    for (const finding of findings) {
      if (this.shouldGroupByAnalysis(finding) && finding.analysisId) {
        const analysisGroupKey = this.encodeAnalysisGroupKey(finding, sampleId);

        if (!sharedAnalyses[analysisGroupKey]) {
          sharedAnalyses[analysisGroupKey] = this.createExistingAnalysisReference(finding, sampleId);
        }
      }
    }

    for (const finding of findings) {
      if (this.shouldGroupByAnalysis(finding) && !finding.analysisId) {
        const analysisGroupKey = this.encodeAnalysisGroupKey(finding, sampleId);
        (groupedFindings[analysisGroupKey] ??= []).push(finding);
      } else {
        parseObjects.push(this.findingMapper.fromDomain(finding, sampleId));
      }
    }

    for (const [analysisGroupKey, groupFindings] of Object.entries(groupedFindings)) {
      const parseAnalysis =
        sharedAnalyses[analysisGroupKey] ?? this.createNewAnalysisFromGroupKey(analysisGroupKey);

      sharedAnalyses[analysisGroupKey] = parseAnalysis;

      for (const finding of groupFindings) {
        parseObjects.push(this.findingMapper.fromDomain(finding, sampleId, { parseAnalysis }));
      }
    }

    return parseObjects;
  }

  private shouldGroupByAnalysis(finding: Finding): boolean {
    return this.groupableFindingTypes.has(finding.type);
  }

  private encodeAnalysisGroupKey(finding: Finding, sampleId: string): string {
    return this.encodeObject({
      assetId: finding.assetId ?? '',
      sampleId,
      pipelineId: finding.pipelineId,
      creatorEntity: finding.creatorEntity,
      creatorId: finding.creatorId,
    });
  }

  private createExistingAnalysisReference(finding: Finding, sampleId: string): Parse.Object {
    const parseSample = this.subclasses.getSubclass(SampleTopology.TABLE).createWithoutData(sampleId);
    const parseAsset = finding.assetId
      ? this.subclasses.getSubclass(AssetTopology.TABLE).createWithoutData(finding.assetId)
      : undefined;
    const parsePipeline = this.subclasses
      .getSubclass(PipelineTopology.TABLE)
      .createWithoutData(finding.pipelineId);

    return this.parse.Object.fromJSON({
      className: AnalysisTopology.TABLE,
      objectId: finding.analysisId,
      [AnalysisTopology.SAMPLE]: parseSample,
      [AnalysisTopology.ASSET]: parseAsset,
      [AnalysisTopology.PIPELINE]: parsePipeline,
    });
  }

  private createNewAnalysisFromGroupKey(analysisGroupKey: string): Parse.Object {
    const { assetId, sampleId, pipelineId, creatorEntity, creatorId } = this.decodeObject(analysisGroupKey);

    return this.createNewAnalysis({
      assetId: assetId || undefined,
      sampleId,
      pipelineId,
      creatorId,
      creatorEntity,
    });
  }

  private createNewAnalysis({
    assetId,
    sampleId,
    pipelineId,
    creatorId,
    creatorEntity,
  }: {
    assetId?: string;
    sampleId: string;
    pipelineId: string;
    creatorId: string;
    creatorEntity: string;
  }): Parse.Object {
    const ParseSample = this.subclasses.getSubclass(SampleTopology.TABLE);
    const ParseAsset = this.subclasses.getSubclass(AssetTopology.TABLE);
    const ParsePipeline = this.subclasses.getSubclass(PipelineTopology.TABLE);
    const ParseAlgorithm = this.subclasses.getSubclass(AlgorithmTopology.TABLE);

    const creatorSubclass = creatorEntity === 'algorithm' ? ParseAlgorithm : this.parse.User;
    const creatorKey = creatorEntity === 'algorithm' ? AnalysisTopology.ALGORITHM : AnalysisTopology.CREATED_BY;

    const parseAnalysis = this.parse.Object.fromJSON({
      className: AnalysisTopology.TABLE,
    });

    parseAnalysis.set({
      [AnalysisTopology.SAMPLE]: ParseSample.createWithoutData(sampleId),
      [AnalysisTopology.ASSET]: assetId ? ParseAsset.createWithoutData(assetId) : undefined,
      [AnalysisTopology.PIPELINE]: ParsePipeline.createWithoutData(pipelineId),
      [creatorKey]: creatorSubclass.createWithoutData(creatorId),
    });

    return parseAnalysis;
  }

  private encodeObject(object: Record<string, string>): string {
    return JSON.stringify(object);
  }

  private decodeObject(string: string): Record<string, string> {
    return JSON.parse(string);
  }
}
